import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';

import { handleSortGalleryImagesByUrl } from '../utils/sort-image-by-url';
import { addIdToGalleryImages } from '../utils/add-id-to-gallery-images';
import { urlParam } from '../utils/url-param';
import { CarViewType, GalleryResponse } from '../types/common-types';

const uId = urlParam('uid');
const mId = urlParam('mid');
const baseUrl = process.env.REACT_APP_BASE_URL;
const galleryUrl = `${baseUrl}/gallery-manager/list/${uId}/${mId}`;

export const fetchCarGallery = createAsyncThunk(
  'car/fetchCarGallery',
  async (quality: number | string) => {
    let data;
    try {
      const response = await fetch(`${galleryUrl}?quality=${quality}`);
      data = await response.json();

      return data;
    } catch (error) {
      console.log('@getGallery error');

      return error;
    }
  },
);

type Image = {
  url: string;
  size: number;
};

type GallerySliceType = {
  // interior gallery qualities
  interiorGalleryLow: Image[];
  interiorGalleryMid: Image[];
  interiorGalleryHigh: Image[];
  // exterior gallery qualities
  exteriorGalleryLow: Image[];
  exteriorGalleryMid: Image[];
  exteriorGalleryHigh: Image[];
  // general gallery stores
  currentGalleryCarId: string;
  galleryZoomStep: 1 | 2;
};

const initialState: GallerySliceType = {
  // interior gallery qualities
  interiorGalleryLow: [],
  interiorGalleryMid: [],
  interiorGalleryHigh: [],
  // exterior gallery qualities
  exteriorGalleryLow: [],
  exteriorGalleryMid: [],
  exteriorGalleryHigh: [],
  // general gallery stores
  currentGalleryCarId: 'exterior-1',
  galleryZoomStep: 1,
};

const gallerySlice = createSlice({
  initialState,
  name: 'gallery',
  reducers: {
    galleryZoomStepSet: (state, { payload }) => {
      state.galleryZoomStep = payload;
    },
    currentGalleryCarIdSet: (state, action) => {
      state.currentGalleryCarId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchCarGallery.fulfilled,
      (state: any, action: { payload: GalleryResponse; meta: any }) => {
        const quality = action.meta?.arg;

        if (!Array.isArray(action.payload)) {
          state.error = 'no model found';
          return;
        }
        action.payload?.filter(
          (gallery: { categoryName: CarViewType; images: Image }) => {
            const { categoryName, images } = gallery;

            if (categoryName === 'interior') {
              if (quality === 'thumbnail') {
                state.interiorGalleryLow = images;
                if (state.interiorGalleryLow.length === 0) {
                  state.currentGalleryCarId = 'exterior-1';
                }
                return;
              }

              if (quality === 30) {
                state.interiorGalleryMid = images;

                if (state.interiorGalleryMid.length === 0) {
                  state.currentGalleryCarId = 'exterior-1';
                }

                return;
              }

              if (quality === 60) {
                //
                state.interiorGalleryMid = images;
                if (state.interiorGalleryMid.length === 0) {
                  state.currentGalleryCarId = 'exterior-1';
                }
                //
                state.interiorGalleryHigh = images;
                return;
              }
            } else if (categoryName === 'exterior') {
              if (quality === 'thumbnail') {
                state.exteriorGalleryLow = images;
                return;
              }

              if (quality === 30) {
                state.exteriorGalleryMid = images;

                return;
              }

              if (quality === 60) {
                //
                state.exteriorGalleryMid = images;
                //
                state.exteriorGalleryHigh = images;
                return;
              }
            }
          },
        );
      },
    );
    builder.addCase(fetchCarGallery.rejected, () => {
      console.log('@getGallery error');
    });
    builder.addCase(fetchCarGallery.pending, () => {});
  },
});

export const getCurrentGalleryCarId = createSelector(
  (state: { gallery: GallerySliceType }) => state.gallery,
  (gallery) => gallery.currentGalleryCarId,
);

export const getGalleryZoomStep = createSelector(
  (state: { gallery: GallerySliceType }) => state.gallery,
  (gallery: any) => gallery.galleryZoomStep,
);

export const getAllGalleryImagesLow = createSelector(
  (state: { gallery: GallerySliceType }) => state.gallery,
  (gallery) => {
    const newInteriorGalleryLow = [...gallery.interiorGalleryLow];
    const newExteriorGalleryLow = [...gallery.exteriorGalleryLow];

    // exterior
    const sortedExteriorGalleryLow = handleSortGalleryImagesByUrl(
      newExteriorGalleryLow,
    );

    const exteriorGalleryLow = addIdToGalleryImages(
      sortedExteriorGalleryLow,
      'exterior',
    );

    // interior
    const sortedInteriorGalleryLow = handleSortGalleryImagesByUrl(
      newInteriorGalleryLow,
    );

    const interiorGalleryLow = addIdToGalleryImages(
      sortedInteriorGalleryLow,
      'interior',
      exteriorGalleryLow.length + 1,
    );

    return { exteriorGalleryLow, interiorGalleryLow };
  },
);

export const getAllGalleryImagesMid = createSelector(
  (state: { gallery: GallerySliceType }) => state.gallery,
  (gallery) => {
    const newInteriorGalleryMid = [...gallery.interiorGalleryMid];
    const newExteriorGalleryMid = [...gallery.exteriorGalleryMid];

    // exterior
    const sortedExteriorGalleryMid = handleSortGalleryImagesByUrl(
      newExteriorGalleryMid,
    );

    const exteriorGalleryMid = addIdToGalleryImages(
      sortedExteriorGalleryMid,
      'exterior',
    );

    // interior
    const sortedInteriorGalleryMid = handleSortGalleryImagesByUrl(
      newInteriorGalleryMid,
    );

    const interiorGalleryMid = addIdToGalleryImages(
      sortedInteriorGalleryMid,
      'interior',
      exteriorGalleryMid.length + 1,
    );

    return { exteriorGalleryMid, interiorGalleryMid };
  },
);

export const getAllGalleryImagesHigh = createSelector(
  (state: { gallery: GallerySliceType }) => state.gallery,
  (gallery) => {
    const newInteriorGalleryHigh = [...gallery.interiorGalleryHigh];
    const newExteriorGalleryHigh = [...gallery.exteriorGalleryHigh];

    // exterior
    const sortedExteriorGalleryHigh = handleSortGalleryImagesByUrl(
      newExteriorGalleryHigh,
    );

    const exteriorGalleryHigh = addIdToGalleryImages(
      sortedExteriorGalleryHigh,
      'exterior',
    );

    // interior
    const sortedInteriorGalleryHigh = handleSortGalleryImagesByUrl(
      newInteriorGalleryHigh,
    );

    const interiorGalleryHigh = addIdToGalleryImages(
      sortedInteriorGalleryHigh,
      'interior',
      exteriorGalleryHigh.length + 1,
    );

    return { exteriorGalleryHigh, interiorGalleryHigh };
  },
);

export default gallerySlice.reducer;

export const { currentGalleryCarIdSet, galleryZoomStepSet } =
  gallerySlice.actions;
